/**
 * HVAC System Detector
 *
 * Detects and reconstructs HVAC systems from parsed IDF documents.
 * Analyzes AirLoopHVAC objects and related components to build visual graphs.
 */

import type { IDFObject } from '../../parser';
import type {
  DetectedComponent,
  DetectedAirLoop,
  NodeConnection,
  HVACDetectionResult,
} from './types';
import { getMappingByIDFClass, isHVACClass } from '../sync/fieldMappings';
import { getComponentDefinition } from '../components';

/**
 * EnergyPlus field indices for common objects
 */
const FIELD_INDICES = {
  AirLoopHVAC: {
    name: 0,
    // Field 1: Controller List Name
    // Field 2: Availability Manager List Name
    // Field 3: Design Supply Air Flow Rate
    branchListName: 4,
    // Field 5: Connector List Name
    supplyInletNode: 6,
    // Field 7: Demand Side Outlet Node Name
    // Field 8: Demand Side Inlet Node Names
    supplyOutletNode: 9,
    demandInletNode: 9, // Same as supply outlet
    demandOutletNode: 6, // Same as supply inlet
  },
  Branch: {
    name: 0,
    // Fields 1-n contain component info in groups of 4:
    // Component Type, Component Name, Inlet Node, Outlet Node
  },
  BranchList: {
    name: 0,
    // Fields 1-n contain branch names
  },
  'AirLoopHVAC:ZoneSplitter': {
    name: 0,
    inletNode: 1,
    // Fields 2-n contain outlet node names
  },
  'AirLoopHVAC:ZoneMixer': {
    name: 0,
    outletNode: 1,
    // Fields 2-n contain inlet node names
  },
  'AirLoopHVAC:SupplyPath': {
    name: 0,
    supplyAirPathInletNode: 1,
    // Component pairs follow
  },
  'AirLoopHVAC:ReturnPath': {
    name: 0,
    returnAirPathOutletNode: 1,
    // Component pairs follow
  },
  'AirLoopHVAC:OutdoorAirSystem': {
    name: 0,
    controllerListName: 1,
    outdoorAirEquipmentListName: 2,
  },
};

/**
 * Get field value from IDF object by index
 */
function getField(obj: IDFObject, index: number): string {
  return obj.fields[index]?.value ?? '';
}

/**
 * Extract component from IDF object
 */
function extractComponent(obj: IDFObject): DetectedComponent | null {
  if (!isHVACClass(obj.className)) {
    return null;
  }

  const mapping = getMappingByIDFClass(obj.className);
  if (!mapping) {
    return null;
  }

  const definition = getComponentDefinition(mapping.componentType);
  if (!definition) {
    return null;
  }

  // Get component name
  const name = getField(obj, mapping.nameFieldIndex);

  // Extract field values
  const fields: Record<string, string> = {};
  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const value = getField(obj, fieldMapping.fieldIndex);
    if (value) {
      fields[fieldName] = value;
    }
  }

  // Extract node connections
  const nodeConnections = new Map<string, string>();
  const inletNodes: string[] = [];
  const outletNodes: string[] = [];

  for (const portMapping of mapping.portMappings) {
    const nodeName = getField(obj, portMapping.fieldIndex);
    if (nodeName) {
      nodeConnections.set(nodeName, portMapping.portId);

      // Categorize by port direction
      const port = definition.ports.find(p => p.id === portMapping.portId);
      if (port) {
        if (port.direction === 'inlet') {
          inletNodes.push(nodeName);
        } else {
          outletNodes.push(nodeName);
        }
      }
    }
  }

  return {
    name,
    idfClass: obj.className,
    componentType: mapping.componentType,
    idfObjectId: obj.id,
    fields,
    nodeConnections,
    inletNodes,
    outletNodes,
  };
}

/**
 * Parse Branch object to extract components
 */
function parseBranch(
  branchObj: IDFObject,
  allObjects: IDFObject[]
): DetectedComponent[] {
  const components: DetectedComponent[] = [];
  const fields = branchObj.fields;

  // Branch fields:
  // Field 0: Name
  // Field 1: Pressure Drop Curve Name
  // Fields 2+ are in groups of 4: Component Type, Name, Inlet Node, Outlet Node
  let fieldIndex = 2;

  while (fieldIndex + 3 < fields.length) {
    const componentType = fields[fieldIndex]?.value ?? '';
    const componentName = fields[fieldIndex + 1]?.value ?? '';
    const inletNode = fields[fieldIndex + 2]?.value ?? '';
    const outletNode = fields[fieldIndex + 3]?.value ?? '';

    if (!componentType || !componentName) {
      fieldIndex += 4;
      continue;
    }

    // Find the actual component object
    const componentObj = allObjects.find(
      obj => obj.className === componentType && getField(obj, 0) === componentName
    );

    if (componentObj) {
      const detected = extractComponent(componentObj);
      if (detected) {
        // Get the component definition to find actual port IDs
        const compDef = getComponentDefinition(detected.componentType);

        // Find the first inlet and outlet port IDs from the component definition
        const inletPortId = compDef?.ports.find(p => p.direction === 'inlet')?.id ?? 'air-inlet';
        const outletPortId = compDef?.ports.find(p => p.direction === 'outlet')?.id ?? 'air-outlet';

        // Override with branch-specified nodes if they differ
        // Also add to nodeConnections Map so buildNodeConnections can find them
        if (inletNode && !detected.inletNodes.includes(inletNode)) {
          detected.inletNodes.push(inletNode);
          // Use the component's actual inlet port ID
          if (!detected.nodeConnections.has(inletNode)) {
            detected.nodeConnections.set(inletNode, inletPortId);
          }
        }
        if (outletNode && !detected.outletNodes.includes(outletNode)) {
          detected.outletNodes.push(outletNode);
          // Use the component's actual outlet port ID
          if (!detected.nodeConnections.has(outletNode)) {
            detected.nodeConnections.set(outletNode, outletPortId);
          }
        }
        components.push(detected);
      }
    }

    fieldIndex += 4;
  }

  return components;
}

/**
 * Detect air loop from AirLoopHVAC object
 */
function detectAirLoop(
  airLoopObj: IDFObject,
  allObjects: IDFObject[]
): DetectedAirLoop | null {
  const name = getField(airLoopObj, FIELD_INDICES.AirLoopHVAC.name);
  if (!name) return null;

  const branchListName = getField(airLoopObj, FIELD_INDICES.AirLoopHVAC.branchListName);
  const supplyInletNode = getField(airLoopObj, FIELD_INDICES.AirLoopHVAC.supplyInletNode);
  const supplyOutletNode = getField(airLoopObj, FIELD_INDICES.AirLoopHVAC.supplyOutletNode);

  // Find the branch list
  const branchList = allObjects.find(
    obj => obj.className === 'BranchList' && getField(obj, 0) === branchListName
  );

  const supplyComponents: DetectedComponent[] = [];

  if (branchList) {
    // Get all branch names from the branch list
    for (let i = 1; i < branchList.fields.length; i++) {
      const branchName = branchList.fields[i]?.value;
      if (!branchName) continue;

      // Find the branch object
      const branch = allObjects.find(
        obj => obj.className === 'Branch' && getField(obj, 0) === branchName
      );

      if (branch) {
        const branchComponents = parseBranch(branch, allObjects);
        supplyComponents.push(...branchComponents);
      }
    }
  }

  // Find supply path for zone splitter
  const supplyPath = allObjects.find(
    obj => obj.className === 'AirLoopHVAC:SupplyPath' &&
      getField(obj, 1) === supplyOutletNode
  );

  let zoneSplitter: string | undefined;
  if (supplyPath) {
    // Look for ZoneSplitter in the supply path
    for (let i = 2; i < supplyPath.fields.length; i += 2) {
      const componentType = supplyPath.fields[i]?.value;
      const componentName = supplyPath.fields[i + 1]?.value;
      if (componentType === 'AirLoopHVAC:ZoneSplitter' && componentName) {
        zoneSplitter = componentName;
        break;
      }
    }
  }

  // Find return path for zone mixer
  const returnPath = allObjects.find(
    obj => obj.className === 'AirLoopHVAC:ReturnPath' &&
      getField(obj, 1) === supplyInletNode
  );

  let zoneMixer: string | undefined;
  if (returnPath) {
    // Look for ZoneMixer in the return path
    for (let i = 2; i < returnPath.fields.length; i += 2) {
      const componentType = returnPath.fields[i]?.value;
      const componentName = returnPath.fields[i + 1]?.value;
      if (componentType === 'AirLoopHVAC:ZoneMixer' && componentName) {
        zoneMixer = componentName;
        break;
      }
    }
  }

  // Detect zone terminals (demand side)
  const demandComponents: DetectedComponent[] = [];

  // Find ZoneHVAC:EquipmentConnections to identify zone terminals
  const zoneEquipConnections = allObjects.filter(
    obj => obj.className === 'ZoneHVAC:EquipmentConnections'
  );

  for (const zoneConn of zoneEquipConnections) {
    // Field 3 is Zone Air Inlet Node or NodeList Name
    const zoneAirInletNode = zoneConn.fields[3]?.value;
    if (!zoneAirInletNode) continue;

    // Find air terminal that supplies to this zone
    const terminalClasses = [
      'AirTerminal:SingleDuct:VAV:Reheat',
      'AirTerminal:SingleDuct:VAV:NoReheat',
      'AirTerminal:SingleDuct:ConstantVolume:NoReheat',
      'AirTerminal:SingleDuct:ConstantVolume:Reheat',
      'AirTerminal:SingleDuct:Uncontrolled',
    ];

    for (const termClass of terminalClasses) {
      const terminals = allObjects.filter(obj => obj.className === termClass);
      for (const terminal of terminals) {
        const detected = extractComponent(terminal);
        if (detected && detected.outletNodes.includes(zoneAirInletNode)) {
          demandComponents.push(detected);
        }
      }
    }
  }

  // Also check if zone splitter connects to any terminals
  if (zoneSplitter) {
    const splitterObj = allObjects.find(
      obj => obj.className === 'AirLoopHVAC:ZoneSplitter' && getField(obj, 0) === zoneSplitter
    );

    if (splitterObj) {
      // Get outlet nodes from splitter (indices 2+)
      for (let i = 2; i < splitterObj.fields.length; i++) {
        const outletNode = splitterObj.fields[i]?.value;
        if (!outletNode) continue;

        // Find any component that has this as inlet
        for (const obj of allObjects) {
          if (!isHVACClass(obj.className)) continue;
          const detected = extractComponent(obj);
          if (detected && detected.inletNodes.includes(outletNode)) {
            // Check if not already in demand components
            if (!demandComponents.find(c => c.idfObjectId === detected.idfObjectId)) {
              demandComponents.push(detected);
            }
          }
        }
      }
    }
  }

  // Check for outdoor air system in supply components
  let outdoorAirSystem: string | undefined;
  for (const comp of supplyComponents) {
    if (comp.idfClass === 'AirLoopHVAC:OutdoorAirSystem') {
      outdoorAirSystem = comp.name;
      break;
    }
  }

  return {
    name,
    idfObjectId: airLoopObj.id,
    supplyInletNode,
    supplyOutletNode,
    demandInletNode: supplyOutletNode,
    demandOutletNode: supplyInletNode,
    supplyComponents,
    demandComponents,
    zoneSplitter,
    zoneMixer,
    outdoorAirSystem,
  };
}

/**
 * Build node connections from detected components
 */
function buildNodeConnections(components: DetectedComponent[]): NodeConnection[] {
  const connections: NodeConnection[] = [];

  // Map of node name to components using it
  const nodeUsage = new Map<string, Array<{ component: DetectedComponent; portId: string; direction: 'inlet' | 'outlet' }>>();

  // Build usage map
  for (const comp of components) {
    for (const [nodeName, portId] of comp.nodeConnections) {
      if (!nodeUsage.has(nodeName)) {
        nodeUsage.set(nodeName, []);
      }

      const isInlet = comp.inletNodes.includes(nodeName);
      nodeUsage.get(nodeName)?.push({
        component: comp,
        portId,
        direction: isInlet ? 'inlet' : 'outlet',
      });
    }
  }

  // Find connections (outlet -> inlet with same node name)
  for (const [nodeName, usages] of nodeUsage) {
    const outlets = usages.filter(u => u.direction === 'outlet');
    const inlets = usages.filter(u => u.direction === 'inlet');

    // Connect each outlet to each inlet with the same node name
    for (const outlet of outlets) {
      for (const inlet of inlets) {
        // Don't connect component to itself
        if (outlet.component.idfObjectId === inlet.component.idfObjectId) continue;

        connections.push({
          nodeName,
          source: {
            componentName: outlet.component.name,
            portId: outlet.portId,
            idfObjectId: outlet.component.idfObjectId,
          },
          target: {
            componentName: inlet.component.name,
            portId: inlet.portId,
            idfObjectId: inlet.component.idfObjectId,
          },
        });
      }
    }
  }

  return connections;
}

/**
 * Detect all HVAC systems from an IDF document
 */
export function detectHVACSystems(objects: IDFObject[]): HVACDetectionResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const airLoops: DetectedAirLoop[] = [];
  const standaloneComponents: DetectedComponent[] = [];
  const allDetectedComponents: DetectedComponent[] = [];

  // Find all AirLoopHVAC objects
  const airLoopObjects = objects.filter(obj => obj.className === 'AirLoopHVAC');

  if (airLoopObjects.length === 0) {
    warnings.push('No AirLoopHVAC objects found in the IDF document');
  }

  // Process each air loop
  for (const airLoopObj of airLoopObjects) {
    try {
      const detected = detectAirLoop(airLoopObj, objects);
      if (detected) {
        airLoops.push(detected);
        allDetectedComponents.push(...detected.supplyComponents);
        allDetectedComponents.push(...detected.demandComponents);
      }
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      errors.push(`Failed to detect air loop: ${error}`);
    }
  }

  // Find standalone HVAC components not in any detected air loop
  const processedIds = new Set(allDetectedComponents.map(c => c.idfObjectId));

  for (const obj of objects) {
    if (processedIds.has(obj.id)) continue;

    const detected = extractComponent(obj);
    if (detected) {
      standaloneComponents.push(detected);
      allDetectedComponents.push(detected);
    }
  }

  // Build connections
  const connections = buildNodeConnections(allDetectedComponents);

  return {
    success: errors.length === 0,
    airLoops,
    standaloneComponents,
    connections,
    errors,
    warnings,
  };
}

/**
 * Get statistics about detected systems
 */
export function getDetectionStats(result: HVACDetectionResult): {
  airLoopCount: number;
  supplyComponentCount: number;
  demandComponentCount: number;
  standaloneCount: number;
  connectionCount: number;
} {
  let supplyComponentCount = 0;
  let demandComponentCount = 0;

  for (const loop of result.airLoops) {
    supplyComponentCount += loop.supplyComponents.length;
    demandComponentCount += loop.demandComponents.length;
  }

  return {
    airLoopCount: result.airLoops.length,
    supplyComponentCount,
    demandComponentCount,
    standaloneCount: result.standaloneComponents.length,
    connectionCount: result.connections.length,
  };
}
